import React, { useEffect, useState, lazy } from 'react';
import ReactDOM from 'react-dom';
// eslint-disable-next-line import/no-unresolved
import { init } from '@module-federation/enhanced/runtime';
import Spinner from './Spinner';
import { envUrlSRPortal } from '../config';
import { getApiTarget } from '../utils/apiTarget';

let federatedHost = null;
export const initFederatedHost = (config) => {
  if (federatedHost) {
    return federatedHost;
  }
  federatedHost = init({
    name: 'host',
    remotes: [
      {
        name: 'srPortal',
        entry: config.scheduleSRPortalScriptPath,
        type: 'var',
      },
    ],
    shared: {
      react: {
        singleton: true,
        get: () => Promise.resolve(() => React),
      },
      'react-dom': {
        singleton: true,
        get: () => Promise.resolve(() => ReactDOM),
      },
    },
    inBrowser: true,
  });
  return federatedHost;
};

export const createFederatedComponent = (config) => {
  return lazy(async () => {
    const host = initFederatedHost(config);
    const module = await host.loadRemote(
      config.remoteModulePath || 'srPortal/SRPortalApp',
    );
    return { default: module.default || module };
  });
};

const useRemoteModuleInjection = (config) => {
  const [RemoteComponent, setRemoteComponent] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadRemoteModule = async () => {
      try {
        const host = initFederatedHost(config);
        const remoteModule = await host.loadRemote(
          config.remoteModulePath || 'srPortal/SRPortalApp',
        );

        if (isMounted) {
          setRemoteComponent(
            () => remoteModule.default || remoteModule,
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error during remote module loading:', error);
      }
    };

    loadRemoteModule();

    return () => {
      isMounted = false;
    };
  }, [config]);

  return RemoteComponent;
};

const useRemoteVersion = (config) => {
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadVersion = async () => {
      try {
        const host = initFederatedHost(config);
        const versionModule = await host.loadRemote(
          'srPortal/Version',
        );
        setVersion(versionModule.default || versionModule);
      } catch (err) {
        setError(err);
        // eslint-disable-next-line no-console
        console.error('Error loading remote version:', err);
      } finally {
        setLoading(false);
      }
    };

    loadVersion();
  }, [config]);

  return { version, loading, error };
};

const SR_PORTAL_CONTAINER_ID = 'sr-portal';

export const ScheduleSRPortal = ({
  config,
  accessToken,
  idToken,
  selectedProviderId,
  pageType,
  logoutUrl,
  requestData,
  patientId,
  patientDetails,
  deviceData,
  serviceRequestId,
  serviceRequestNumber,
  hideInternalNavigation,
  ...props
}) => {
  const RemoteComponent = useRemoteModuleInjection(config);
  const { version } = useRemoteVersion(config);
  if (version) {
    // eslint-disable-next-line no-console
    console.log('SR Portal version:', version.version);
  }

  // TODO: Remove temporary logging
  // eslint-disable-next-line no-console
  console.log('ScheduleSRPortal - props received:', {
    patientId,
    patientDetails,
    deviceData,
    pageType,
    selectedProviderId,
    serviceRequestNumber,
    spreadProps: props,
  });

  return (
    <div id={SR_PORTAL_CONTAINER_ID}>
      {RemoteComponent ? (
        <RemoteComponent
          {...props}
          accessToken={accessToken}
          idToken={idToken}
          selectedProviderId={selectedProviderId}
          remoteCheck={config.remoteCheck ?? false}
          pageType={pageType}
          logoutUrl={logoutUrl}
          requestData={requestData}
          patientId={patientId}
          patientDetails={patientDetails}
          deviceData={deviceData}
          selectedId={serviceRequestId}
          selectedSrNumber={serviceRequestNumber}
          apiBaseUrl={config?.apiBaseUrl || envUrlSRPortal}
          hideInternalNavigation={hideInternalNavigation}
          apiTarget={getApiTarget()}
        />
      ) : (
        <Spinner loading />
      )}
    </div>
  );
};
